import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BellRing, Check, X } from "lucide-react";

const aed = (n) => `AED ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export function ReferralProgress({ progress, threshold, testid }) {
  const pct = threshold > 0 ? Math.min(100, Math.round((progress / threshold) * 100)) : 0;
  return (
    <div className="min-w-[140px]" data-testid={testid}>
      <div className="text-xs font-medium">{aed(progress)} <span style={{ color: "var(--text-muted)" }}>/ {aed(threshold)}</span></div>
      <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-soft)" }}>
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: pct >= 100 ? "#1f7a4a" : "var(--gold)" }} />
      </div>
    </div>
  );
}

function RejectDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/referrals/${target.referral_id}/approval`, { action: "reject", reason });
      toast.success("Referral credit rejected. You can review it again later.");
      onDone();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="referral-reject-dialog">
        <DialogHeader><DialogTitle>Reject referral credit</DialogTitle></DialogHeader>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {target.referee_name} (referred by {target.referrer_name}). The referral stays open, so you can approve it later within the validity period.
        </p>
        <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="referral-reject-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || reason.trim().length < 3} onClick={submit} className="bg-red-600 text-white hover:bg-red-700"
                  data-testid="referral-reject-confirm">{busy ? "Saving…" : "Reject"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalRow({ r, busy, onApprove, onReject }) {
  const rejected = r.status === "rejected";
  return (
    <tr className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`referral-approval-row-${r.referral_id}`}>
      <td className="px-4 py-3">
        <div className="font-medium">{r.referee_name}</div>
        <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{r.referee_id} · {r.referee_points} pts</div>
      </td>
      <td className="px-4 py-3">
        <div className="font-medium">{r.referrer_name}</div>
        <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{r.referrer_id}{r.referrer_is_vendor ? " · vendor" : ` · ${r.referrer_points} pts`}</div>
      </td>
      <td className="px-4 py-3"><ReferralProgress progress={r.progress_aed} threshold={r.threshold_aed} testid={`referral-approval-progress-${r.referral_id}`} /></td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 rounded-full text-xs ${rejected ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}
              data-testid={`referral-approval-status-${r.referral_id}`}>{rejected ? "Rejected" : "Eligible for approval"}</span>
        {rejected && r.rejection_reason && <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>“{r.rejection_reason}” · {r.rejected_by}</div>}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <Button size="sm" disabled={busy} className="rounded-full h-8 text-white mr-1" style={{ background: "#1f7a4a" }}
                onClick={() => onApprove(r)} data-testid={`referral-approve-${r.referral_id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
        {!rejected && <Button size="sm" variant="outline" disabled={busy} className="rounded-full h-8" onClick={() => onReject(r)}
                              data-testid={`referral-reject-${r.referral_id}`}><X className="h-3.5 w-3.5 mr-1" />Reject</Button>}
      </td>
    </tr>
  );
}

export default function ReferralApprovalsCard({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const load = useCallback(() => api.get("/referrals/approvals").then(({ data }) => setData(data)).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const done = () => { setTarget(null); load(); onChanged?.(); };
  const approve = async (r) => {
    setBusy(true);
    try { await api.post(`/referrals/${r.referral_id}/approval`, { action: "approve" }); toast.success("Referral points credited"); done(); }
    catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="mb-8" data-testid="referral-approvals-card">
      <h2 className="text-xl font-bold flex items-center gap-2 mb-3" style={{ letterSpacing: "-0.01em" }}>
        <BellRing className="h-5 w-5 gold" /> Referral approvals
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
              data-testid="referral-approvals-count">{data.eligible_count} eligible</span>
      </h2>
      <div className="w-card w-card-bordered overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr style={{ background: "var(--bg-soft)" }}>
            {["Referee", "Referrer", "Purchases vs threshold", "Status", ""].map((h, i) => <th key={i} className="px-4 py-3 label-caps text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {!data.rows.length && <tr><td colSpan={5} className="p-6 text-center" style={{ color: "var(--text-muted)" }} data-testid="referral-approvals-empty">
              No referrals are waiting for approval.</td></tr>}
            {data.rows.map((r) => <ApprovalRow key={r.referral_id} r={r} busy={busy} onApprove={approve} onReject={setTarget} />)}
          </tbody>
        </table>
      </div>
      {target && <RejectDialog target={target} onClose={() => setTarget(null)} onDone={done} />}
    </section>
  );
}
